(() => {
  if (window.sylavieCategoryMenuControllerLoaded) return;

  window.sylavieCategoryMenuControllerLoaded = true;

  const SELECTORS = {
    section: '[data-category-menu]',
    nav: '.category-menu__nav',
    trigger: '[data-category-menu-trigger]',
    image: '[data-category-menu-image]',
    autoplayToggle: '[data-category-menu-autoplay-toggle]',
  };

  const ACTIVE_CLASS = 'is-active';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new WeakMap();

  class CategoryMenu {
    constructor(section) {
      this.section = section;
      this.nav = section.querySelector(SELECTORS.nav);
      this.triggers = Array.from(section.querySelectorAll(SELECTORS.trigger));
      this.images = Array.from(section.querySelectorAll(SELECTORS.image));
      this.autoplayToggle = section.querySelector(SELECTORS.autoplayToggle);
      this.activeIndex = this.getInitialActiveIndex();
      this.intervalId = null;
      this.restartTimeoutId = null;
      this.autoplayEnabled = section.dataset.autoplay === 'true';
      this.isPausedByUser = this.autoplayEnabled && reduceMotion.matches;
      this.reducedMotionPaused = this.isPausedByUser;
      this.reducedMotionOverride = false;
      this.autoplayInterval = Math.max(parseInt(section.dataset.autoplayInterval, 10) || 5000, 3000);
      this.autoplayRestartDelay = Math.max(parseInt(section.dataset.autoplayRestartDelay, 10) || 10000, 3000);

      if (!this.triggers.length) return;

      this.onActivate = (event) => {
        const trigger = event.currentTarget;
        this.pauseForInteraction();
        this.activate(trigger.dataset.categoryIndex);
      };
      this.onAutoplayToggleClick = () => {
        if (this.isPausedByUser) this.playAutoplay();
        else this.pauseAutoplay();
      };
      this.onNavMouseEnter = () => this.pauseForInteraction();
      this.onNavMouseLeave = () => this.scheduleAutoplayRestart();
      this.onFocusOut = () => {
        window.requestAnimationFrame(() => {
          if (!this.nav?.contains(document.activeElement)) this.scheduleAutoplayRestart();
        });
      };
      this.onVisibilityChange = () => {
        if (document.hidden) {
          this.stopAutoplay();
          return;
        }

        if (!this.isPausedByUser) this.startAutoplay();
      };
      this.onReduceMotionChange = (event) => {
        if (event.matches) {
          this.stopAutoplay();
          this.clearRestartTimeout();
          if (!this.reducedMotionOverride) {
            this.isPausedByUser = true;
            this.reducedMotionPaused = true;
            this.updateAutoplayToggle();
          }
          return;
        }

        this.autoplayEnabled = this.section.dataset.autoplay === 'true';
        if (this.reducedMotionPaused) {
          this.isPausedByUser = false;
          this.reducedMotionPaused = false;
          this.updateAutoplayToggle();
        }
        if (!this.isPausedByUser) this.startAutoplay();
      };

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('mouseenter', this.onActivate);
        trigger.addEventListener('focus', this.onActivate);
      });

      this.autoplayToggle?.addEventListener('click', this.onAutoplayToggleClick);
      this.nav?.addEventListener('mouseenter', this.onNavMouseEnter);
      this.nav?.addEventListener('mouseleave', this.onNavMouseLeave);
      this.nav?.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      reduceMotion.addEventListener('change', this.onReduceMotionChange);

      this.updateAutoplayToggle();
      this.startAutoplay();
    }

    destroy() {
      this.stopAutoplay();
      this.clearRestartTimeout();
      this.triggers.forEach((trigger) => {
        trigger.removeEventListener('mouseenter', this.onActivate);
        trigger.removeEventListener('focus', this.onActivate);
      });
      this.autoplayToggle?.removeEventListener('click', this.onAutoplayToggleClick);
      this.nav?.removeEventListener('mouseenter', this.onNavMouseEnter);
      this.nav?.removeEventListener('mouseleave', this.onNavMouseLeave);
      this.nav?.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      reduceMotion.removeEventListener('change', this.onReduceMotionChange);
    }

    getInitialActiveIndex() {
      const activeTrigger = this.triggers.find((trigger) => trigger.classList.contains(ACTIVE_CLASS));

      return activeTrigger?.dataset.categoryIndex || this.triggers[0]?.dataset.categoryIndex || null;
    }

    getNextIndex() {
      const currentPosition = this.triggers.findIndex((trigger) => trigger.dataset.categoryIndex === this.activeIndex);
      const nextPosition = currentPosition >= 0 ? (currentPosition + 1) % this.triggers.length : 0;

      return this.triggers[nextPosition]?.dataset.categoryIndex;
    }

    startAutoplay() {
      this.stopAutoplay();

      if (
        !this.autoplayEnabled ||
        this.isPausedByUser ||
        (reduceMotion.matches && !this.reducedMotionOverride) ||
        document.hidden ||
        this.triggers.length <= 1
      )
        return;

      this.intervalId = window.setInterval(() => {
        this.activate(this.getNextIndex());
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }

    pauseForInteraction() {
      this.stopAutoplay();
      this.clearRestartTimeout();
    }

    scheduleAutoplayRestart() {
      if (
        !this.autoplayEnabled ||
        this.isPausedByUser ||
        (reduceMotion.matches && !this.reducedMotionOverride) ||
        this.triggers.length <= 1
      )
        return;

      this.clearRestartTimeout();
      this.restartTimeoutId = window.setTimeout(() => {
        this.startAutoplay();
      }, this.autoplayRestartDelay);
    }

    clearRestartTimeout() {
      window.clearTimeout(this.restartTimeoutId);
      this.restartTimeoutId = null;
    }

    pauseAutoplay() {
      this.isPausedByUser = true;
      this.reducedMotionPaused = false;
      this.stopAutoplay();
      this.clearRestartTimeout();
      this.updateAutoplayToggle();
    }

    playAutoplay() {
      this.isPausedByUser = false;
      this.reducedMotionPaused = false;
      this.reducedMotionOverride = true;
      this.clearRestartTimeout();
      this.startAutoplay();
      this.updateAutoplayToggle();
    }

    updateAutoplayToggle() {
      if (!this.autoplayToggle) return;

      const pauseLabel = this.autoplayToggle.dataset.pauseLabel || 'Pause category rotation';
      const playLabel = this.autoplayToggle.dataset.playLabel || 'Play category rotation';
      this.autoplayToggle.setAttribute('aria-label', this.isPausedByUser ? playLabel : pauseLabel);
      this.autoplayToggle.classList.toggle('is-paused', this.isPausedByUser);
    }

    activate(index) {
      if (!index) return;

      const matchingImages = this.images.filter((image) => image.dataset.categoryIndex === index);

      this.triggers.forEach((trigger) => {
        trigger.classList.toggle(ACTIVE_CLASS, trigger.dataset.categoryIndex === index);
      });

      this.activeIndex = index;

      if (!matchingImages.length) return;

      this.images.forEach((image) => {
        const isActive = image.dataset.categoryIndex === index;

        image.classList.toggle(ACTIVE_CLASS, isActive);
        image.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }
  }

  const initCategoryMenus = (root = document) => {
    const sections = root.matches?.(SELECTORS.section)
      ? [root]
      : Array.from(root.querySelectorAll(SELECTORS.section));

    sections.forEach((section) => {
      const existingInstance = instances.get(section);
      if (existingInstance) existingInstance.destroy();

      instances.set(section, new CategoryMenu(section));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCategoryMenus());
  } else {
    initCategoryMenus();
  }

  document.addEventListener('shopify:section:load', (event) => initCategoryMenus(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(SELECTORS.section)
      ? event.target
      : event.target.querySelector(SELECTORS.section);
    const instance = section ? instances.get(section) : null;

    if (!instance) return;

    instance.destroy();
    instances.delete(section);
  });
})();
